// biome-ignore format: historic
// biome-ignore format: historic
// biome-ignore format: historic
import { editedCardFieldsContext } from '@context/edited-card-fields-context';
import { PopoverContext } from '@context/popover-context';
import useNode from '@hooks/use-node';
import { getSuggestionsByFieldType } from '@modules/card-helpers';
import { Stack, TextArea, TextField } from '@ui-lib';
import { useContext, useEffect } from 'react';
import type { CardAddress, CardPresetAddress } from 'types';

import LinkStatusContainer from '../LinkStatusContainer/LinkStatusContainer';
import SuggestionsSection, {
  useSuggestionsSection,
} from '../SuggestionsSection/SuggestionsSection';

interface AddressEditPopoverProps<T> {
  field: T;
  onChange: (field: T) => void;
  linked?: boolean;
  mode: 'create' | 'update' | undefined;
}

function AddressEditPopover<T extends CardAddress | CardPresetAddress>({
  field,
  onChange,
  linked = false,
}: AddressEditPopoverProps<T>): React.ReactElement {
  const [valueInputNode, valueInputRef] = useNode<HTMLTextAreaElement>();
  const { show } = useContext(PopoverContext);
  const { onEditAttribute } = useContext(editedCardFieldsContext);
  const options = getSuggestionsByFieldType('address')();
  const [selectedOption, setSelectedOption] = useSuggestionsSection(field.label, options);

  useEffect(() => {
    if (show && valueInputNode) {
      const intersectionObserver = new IntersectionObserver((entries, observer) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setTimeout(() => {
            valueInputNode.focus();
          }, 150);
          observer.unobserve(entry.target);
        }
      });

      intersectionObserver.observe(valueInputNode);
    }
  }, [show, valueInputNode]);

  function handleLabelChange(label: string) {
    onChange({ ...field, label });
  }

  function handleValueChange(value: string) {
    onEditAttribute(field.id);
    onChange({ ...field, value });
  }

  function handleSelectSuggestion(suggestion: string) {
    const newSelectedOption = suggestion === selectedOption ? '' : suggestion;

    setSelectedOption(newSelectedOption);
    onChange({ ...field, label: newSelectedOption });
  }

  return (
    <Stack gap="large">
      <LinkStatusContainer linked={linked}>
        <TextArea
          ref={valueInputRef}
          value={field.value}
          onChange={(event) => handleValueChange(event.currentTarget.value)}
          label="Value"
        />
      </LinkStatusContainer>
      <TextField
        value={field.label}
        onChange={(event) => handleLabelChange(event.currentTarget.value)}
        label="Label (Optional)"
      />
      <SuggestionsSection
        options={options}
        onSelect={handleSelectSuggestion}
        selectedOption={selectedOption}
        subject="label"
      />
    </Stack>
  );
}

export default AddressEditPopover;
